import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftComponents/SoftBox';
import SoftTypography from 'components/SoftComponents/SoftTypography';

import { getAllDevices } from 'services/api/bks';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";
import { getUserAccessLevel } from 'utils/setup';

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import cameraInactive from 'assets/images/camera_inactive.png';
import { useSoftUIController } from "hooks/context";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

const BATTERY_THRESHOLD = 25;

function setFeatures(group) {
    var features = group.geojson;

    features.forEach(function (feature) {
        feature.properties = {
            group_id: group.group_id,
            name: group.name,
            parent_id: group.parent_id
        };
    });

    return features;
}

function setDeviceFeatures(device) {

    var features = [
        {
            type: "Feature",
            properties: {
                name: device.name,
                uuid: device.uuid
            },
            geometry: {
                type: 'Point',
                coordinates: device.position
            }
        }
    ];

    return features;
}

function DeviceMap({ group = null, user_type = "viewer", setNavTitle, selected_device, padding }) {

    const mapContainer = useRef(null);
    const map = useRef(null);

    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout } = controller;

    useEffect(() => {
        setNavTitle(['devices', selected_device.name]);

        if (map.current) {
            return;
        }

        var bounds = bbox({
            "type": "FeatureCollection",
            "features": [
                {
                    "type": "Feature",
                    "properties": {},
                    "geometry": {
                        "coordinates": selected_device.position,
                        "type": "Point"
                    }
                }
            ]
        });

        var polygon = bboxPolygon(bounds);

        var map_settings = {
            style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
            zoom: 8,
            maxZoom: 19
        };

        map_settings.container = mapContainer.current;
        map_settings.cooperativeGestures = deviceLayout == 'mobile';
        map_settings.center = center({
            "type": "FeatureCollection",
            "features": group.geojson
        }).geometry.coordinates;

        map.current = new mapboxgl.Map(map_settings);
        map.current.fitBounds(polygon.bbox, {
            linear: true,
            padding: padding,
            duration: 1500
        });

    }, [setNavTitle]);

    useEffect(() => { //mount
        if (map.current) {
            map.current.on('load', () => {
                var existing_online_image = map.current.hasImage('cameraIconOnline');
                var existing_warning_image = map.current.hasImage('cameraIconWarning');
                var existing_offline_image = map.current.hasImage('cameraIconOffline');
                var existing_inactive_image = map.current.hasImage('cameraIconInactive');

                map.current.loadImage(
                    cameraOnline, (error, image) => {
                        if (error) throw error;
                        if (!existing_online_image) {
                            map.current.addImage('cameraIconOnline', image);
                        }
                    }
                );
                map.current.loadImage(
                    cameraWarning, (error, image) => {
                        if (error) throw error;
                        if (!existing_warning_image) {
                            map.current.addImage('cameraIconWarning', image);
                        }
                    }
                );
                map.current.loadImage(
                    cameraOffline, (error, image) => {
                        if (error) throw error;
                        if (!existing_offline_image) {
                            map.current.addImage('cameraIconOffline', image);
                        }
                    }
                );
                map.current.loadImage(
                    cameraInactive, (error, image) => {
                        if (error) throw error;
                        if (!existing_inactive_image) {
                            map.current.addImage('cameraIconInactive', image);
                        }
                    }
                );

                var existing_base_layer = map.current.getLayer('geojson-base');
                var existing_base_source = map.current.getSource('geojson-base');

                if (!existing_base_source) {
                    map.current.addSource('geojson-base', {
                        type: 'geojson',
                        data: {
                            "type": "FeatureCollection",
                            "features": setFeatures(group)
                        }
                    });
                }
                if (!existing_base_layer) {
                    map.current.addLayer({
                        id: 'geojson-base',
                        type: 'fill',
                        source: 'geojson-base',
                        paint: {
                            "fill-opacity": 0.1,
                            "fill-color": "black"
                        }
                    }).addLayer({
                        id: 'geojson-border-base',
                        type: 'line',
                        source: 'geojson-base',
                        paint: {
                            "line-color": "black",
                            "line-width": 2,
                            "line-opacity": 0.2
                        }
                    });
                }

                if (group.subgroups.length > 0) {
                    group.subgroups.forEach(function (subgroup) {
                        if (subgroup.group_id != group.group_id) {
                            var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                            var existing_group_source = map.current.getSource('geojson-' + subgroup.group_id);

                            if (!existing_group_source) {
                                map.current.addSource('geojson-' + subgroup.group_id, {
                                    type: 'geojson',
                                    data: {
                                        "type": "FeatureCollection",
                                        "features": setFeatures(subgroup)
                                    }
                                });
                            }
                            if (!existing_group_layer) {
                                map.current.addLayer({
                                    id: 'geojson-' + subgroup.group_id,
                                    type: 'line',
                                    source: 'geojson-' + subgroup.group_id,
                                    paint: {
                                        "line-color": "black",
                                        "line-width": 4,
                                        "line-opacity": 0.4
                                    }
                                });
                            }
                        }
                    })
                }

                group.devices.forEach(function (device) {
                    var existing_device_layer = map.current.getLayer('geojson-' + device.uuid);
                    var existing_device_source = map.current.getSource('geojson-' + device.uuid);

                    var colour = 'grey';

                    if (['1003', '1004', '1005', '1006', '1007', 'Mercury Carpark (1266)'].indexOf(device.name) > -1 || group.type == "camera") {

                        if (!device.active && getUserAccessLevel(user_type, 'technician')) {
                            var icon_image = 'cameraIconInactive';
                        }
                        else {
                            if (device.connected_state == 'offline') {
                                var icon_image = 'cameraIconOffline';
                            }
                            else if (device.battery <= BATTERY_THRESHOLD && getUserAccessLevel(user_type, 'technician')) {
                                var icon_image = 'cameraIconWarning';
                            }
                            else {
                                var icon_image = 'cameraIconOnline';
                            }
                        }

                        if (!existing_device_source) {
                            map.current.addSource('device-' + device.uuid, {
                                type: 'geojson',
                                data: {
                                    type: 'FeatureCollection',
                                    features: setDeviceFeatures(device)
                                }
                            });
                        }

                        if (!existing_device_layer) {
                            map.current.addLayer({
                                id: 'device-' + device.uuid,
                                type: 'symbol',
                                source: 'device-' + device.uuid,
                                layout: {
                                    'icon-image': icon_image,
                                    'icon-size': 0.044
                                }
                            });
                        }
                    }
                    else {
                        if (selected_device.uuid == device.uuid) {
                            if (!selected_device.active && getUserAccessLevel(user_type, 'technician')) {
                                colour = '#555555';
                            }
                            else {
                                if (selected_device.connected_state != "online") {
                                    colour = "#ff667c";
                                }
                                else if (selected_device.battery <= BATTERY_THRESHOLD && getUserAccessLevel(user_type, 'technician')) {
                                    colour = "#edaa00";
                                }
                                else {
                                    colour = "#58ba2d";
                                }
                            }
                        }

                        if (!existing_device_source) {
                            map.current.addSource('device-' + device.uuid, {
                                type: 'geojson',
                                data: {
                                    "type": "FeatureCollection",
                                    "features": setDeviceFeatures(device)
                                }
                            });
                        }
                        if (!existing_device_layer) {
                            map.current.addLayer({
                                id: 'device-' + device.uuid,
                                type: 'circle',
                                source: 'device-' + device.uuid,
                                paint: {
                                    'circle-color': colour,
                                    'circle-radius': 9,
                                    'circle-stroke-width': 3,
                                    'circle-stroke-color': '#ffffff'
                                }
                            });
                        }
                    }
                });
            });
        }
    }, []);

    useEffect(() => () => { //unmount

        var existing_base_layer = map.current.getLayer('geojson-base');
        if (existing_base_layer) {
            map.current.removeLayer('geojson-base');
            map.current.removeLayer('geojson-border-base');
            map.current.removeSource('geojson-base');
        }

        if (group.subgroups.length > 0) {
            group.subgroups.forEach(function (subgroup) {
                var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                if (existing_group_layer) {
                    map.current.removeLayer('geojson-' + subgroup.group_id);
                    map.current.removeSource('geojson-' + subgroup.group_id);
                }
            });
        }

        group.devices.forEach(function (device) {
            var existing_device_layer = map.current.getLayer('device-' + device.uuid);
            // console.log(existing_device_layer);
            if (existing_device_layer) {
                map.current.removeLayer('device-' + device.uuid);
                map.current.removeSource('device-' + device.uuid);
            }
        });

        map.current.remove();

    }, []);

    return (
        <Box component="div" ref={mapContainer} sx={{ height: '100%' }} />
    );
}

DeviceMap.propTypes = {
    group: PropTypes.object,
    user_type: PropTypes.string,
    setNavTitle: PropTypes.func,
    selected_device: PropTypes.object,
    padding: PropTypes.number
};

export default DeviceMap;